import React, { useEffect, useState } from 'react';

interface GTMScriptsProps {
  gtmContainerId?: string;
}

// Extend Window interface for GTM flag
declare global {
  interface Window {
    __GTM_ENABLED__?: boolean;
  }
}

/**
 * GTM head script - initializes dataLayer and loads GTM after hydration
 * Should be placed in the <head> section
 */
export function GTMHeadScript({ gtmContainerId }: GTMScriptsProps): React.ReactElement | null {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!gtmContainerId || !isHydrated) return;

    // Set global flag to enable dataLayer pushes
    window.__GTM_ENABLED__ = true;

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];

    // Load GTM script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmContainerId}`;

    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    document.head.appendChild(script);
    // No cleanup - GTM should persist for the lifetime of the page
  }, [gtmContainerId, isHydrated]);

  // Render nothing - GTM is loaded via useEffect after hydration
  return null;
}

/**
 * GTM noscript fallback - renders the noscript iframe
 * Should be placed immediately after the opening <body> tag
 */
export function GTMNoScript({ gtmContainerId }: GTMScriptsProps): React.ReactElement | null {
  if (!gtmContainerId) return null;

  return (
    <noscript>
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${gtmContainerId}`}
        height="0"
        width="0"
        style={{ display: 'none', visibility: 'hidden' }}
        title="Google Tag Manager"
      />
    </noscript>
  );
}
